import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CurrencyGbp, Bell, WarningCircle, Warning, ArrowRight } from '@phosphor-icons/react';
import { fetchMoneyRequiringAttention, type MoneyRequiringAttention as Data, type MoneyAttentionItem } from '@/lib/mattersApi';
import { useAuthStore } from '@/features/auth/store';

const HEALTH_DOT: Record<string, string> = {
  at_risk: 'bg-red-500',
  needs_attention: 'bg-amber-500',
  healthy: 'bg-emerald-500',
};

const ACTION_ICON: Record<string, typeof Bell> = {
  send_reminder: Bell,
  follow_up: WarningCircle,
  call_solicitor: Warning,
};

export function MoneyRequiringAttention() {
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : user?.memberType === 'admin' ? '/admin' : '/barrister';

  const q = useQuery<Data>({
    queryKey: ['money-requiring-attention'],
    queryFn: fetchMoneyRequiringAttention,
    staleTime: 60_000,
  });

  const data = q.data;
  if (!data || data.attentionCount === 0) return null; // quiet when nothing needs chasing

  const money = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: data.currency, maximumFractionDigits: 0 }).format(n);

  return (
    <section className="rounded-md border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-tight text-foreground">Money requiring attention</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {money(data.totalRequiringAttention)} · {data.attentionCount} matter{data.attentionCount === 1 ? '' : 's'} · recommended today
          </p>
        </div>
        <CurrencyGbp size={18} weight="duotone" className="text-muted-foreground" />
      </div>

      <ul className="divide-y divide-border">
        {data.items.map((item) => (
          <Row key={item.matterId} item={item} basePath={basePath} money={money} />
        ))}
      </ul>
    </section>
  );
}

function Row({ item, basePath, money }: { item: MoneyAttentionItem; basePath: string; money: (n: number) => string }) {
  const ActionIcon = ACTION_ICON[item.recommendedAction.type] ?? Bell;
  const detail = [
    item.overdueDays > 0 ? `overdue ${item.overdueDays}d` : item.sentDaysAgo !== null ? `sent ${item.sentDaysAgo}d ago` : null,
    item.remindersSent > 0 ? `${item.remindersSent} reminder${item.remindersSent === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <li>
      <Link
        to={`${basePath}/matters/${item.matterId}?tab=fees`}
        className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/40"
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${HEALTH_DOT[item.health] ?? 'bg-muted'}`} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-xs font-medium text-foreground">{item.caseName}</span>
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{money(item.outstanding)}</span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {[item.clientName, detail].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-brand-600 dark:text-brand-400">
          <ActionIcon size={12} weight="bold" />
          {item.recommendedAction.label}
          <ArrowRight size={11} weight="bold" />
        </span>
      </Link>
    </li>
  );
}
